import { useState } from "react";
import { libraryConnectionsOf, saveToLibraryChoices, libraryDiff, connectionSummary } from "../../utils/equipmentLibrary";

// P2-C — the two explicit Equipment Library actions on the Assumed Equipment
// page. Both are copies, never links: Add from Library creates an independent
// project snapshot; Save to Library writes library defaults for FUTURE
// projects only and never touches equipment already copied into any estimate.

const btn = { fontSize: 11.5, fontWeight: 700, padding: "3px 9px", border: "1px solid var(--cs-border)", background: "var(--surface)", color: "var(--text2)", borderRadius: 6, cursor: "pointer", whiteSpace: "nowrap" };
const btnPrimary = { ...btn, border: "1px solid #2563eb", background: "#2563eb", color: "#fff" };
const inp = { fontSize: 12, border: "1px solid var(--cs-border)", borderRadius: 5, padding: "3px 6px", background: "var(--surface)", color: "var(--text)", boxSizing: "border-box" };

// "Add from library" — pick an active library item; the parent receives the
// item and its library connections and performs the copy.
export function AddFromLibraryBar({ library = [], libraryConnections = [], disabled = false, onAdd }) {
  const [pick, setPick] = useState("");
  const active = library.filter(l => l.IsActive !== false && l.IsActive !== 0);
  const item = active.find(l => String(l.LibraryItemID) === pick) || null;
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
      <span style={{ fontSize: 11.5, color: "var(--text3)" }}>or copy from the library:</span>
      <select aria-label="Library item" value={pick} onChange={e => setPick(e.target.value)} style={{ ...inp, width: 340 }} disabled={disabled || !active.length}>
        <option value="">{active.length ? "— pick a library item —" : "— library is empty — save a project record to it first —"}</option>
        {active.map(l => (
          <option key={l.LibraryItemID} value={l.LibraryItemID}>
            {[l.Category, l.Description, l.Manufacturer, l.Model].filter(Boolean).join(" · ")} ({libraryConnectionsOf(l, libraryConnections).length} conn)
          </option>
        ))}
      </select>
      <button onClick={() => { if (item) { onAdd(item, libraryConnectionsOf(item, libraryConnections)); setPick(""); } }} disabled={disabled || !item} style={btn}
        title="Creates an independent copy of the item's header and connections in this revision. Later library edits never change the copy; the copy remembers which item it came from.">
        Add from library
      </button>
    </div>
  );
}

// "Save to library" — the choice is never implicit. Unlinked rows can only
// create; linked rows choose Update (with a diff to confirm) or Create new.
export function SaveToLibraryDialog({ eq, connections = [], libraryById, libraryConnections = [], onChoose, onCancel }) {
  const { choices, linked, linkedMissing } = saveToLibraryChoices(eq, libraryById);
  const [step, setStep] = useState(choices.length === 1 ? "confirm-create" : "choose");
  const diff = linked ? libraryDiff(eq, connections, linked, libraryConnections) : null;
  const mine = connections.filter(c => c.EquipmentID === eq.EquipmentID);
  const title = `${eq.Tag ? `${eq.Tag} — ` : ""}${eq.Description}`;

  return (
    <div role="dialog" aria-label="Save to library" style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.35)", zIndex: 400, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={onCancel}>
      <div onClick={e => e.stopPropagation()} style={{ background: "var(--surface)", borderRadius: 10, padding: 18, width: 560, maxWidth: "94vw", maxHeight: "86vh", overflowY: "auto", boxShadow: "0 12px 40px rgba(0,0,0,0.25)", fontSize: 12.5 }}>
        <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 4 }}>Save "{title}" to the Equipment Library</div>
        <div style={{ fontSize: 11.5, color: "var(--text3)", marginBottom: 12 }}>
          Library items are defaults for future projects. Saving never changes equipment already copied into this or any other estimate.
        </div>

        {step === "choose" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <button onClick={() => setStep("confirm-update")} style={{ ...btn, textAlign: "left", padding: "8px 10px" }}>
              <div style={{ fontWeight: 800 }}>Update linked library item</div>
              <div style={{ fontSize: 11, color: "var(--text3)", fontWeight: 400 }}>"{linked.Description}"{linked.Manufacturer ? ` · ${linked.Manufacturer}` : ""}{linked.Model ? ` ${linked.Model}` : ""} — you will see the changes before anything is written.</div>
            </button>
            <button onClick={() => setStep("confirm-create")} style={{ ...btn, textAlign: "left", padding: "8px 10px" }}>
              <div style={{ fontWeight: 800 }}>Create as new library item</div>
              <div style={{ fontSize: 11, color: "var(--text3)", fontWeight: 400 }}>Leaves "{linked.Description}" unchanged; this record will point at the new item.</div>
            </button>
            <div><button onClick={onCancel} style={btn}>Cancel</button></div>
          </div>
        )}

        {step === "confirm-create" && (
          <div>
            {linkedMissing && <div style={{ fontSize: 11.5, color: "#92400e", background: "var(--tint-amber)", borderRadius: 6, padding: "5px 8px", marginBottom: 8 }}>This record came from library item #{eq.LibraryItemID}, which is no longer in the active library — only a new item can be created.</div>}
            <div style={{ marginBottom: 6 }}>A <strong>new</strong> library item will be created from this record:</div>
            <ul style={{ margin: "0 0 10px", paddingLeft: 18 }}>
              <li>{[eq.Category, eq.Description, eq.Manufacturer, eq.Model].filter(Boolean).join(" · ") || eq.Description}</li>
              <li>{mine.length} connection{mine.length === 1 ? "" : "s"}{mine.length ? `: ${mine.map(connectionSummary).join("; ")}` : ""}</li>
              <li style={{ color: "var(--text3)" }}>Dimensions / weight are not stored on project equipment yet — the new item starts without them.</li>
            </ul>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => onChoose("create")} style={btnPrimary}>Create new library item</button>
              {choices.length > 1 && <button onClick={() => setStep("choose")} style={btn}>← Back</button>}
              <button onClick={onCancel} style={btn}>Cancel</button>
            </div>
          </div>
        )}

        {step === "confirm-update" && diff && (
          <div>
            <div style={{ marginBottom: 6 }}>Changes that will be written to <strong>"{linked.Description}"</strong> (library item #{linked.LibraryItemID}):</div>
            {!diff.hasChanges && <div style={{ fontSize: 11.5, color: "var(--text3)", marginBottom: 8 }}>No material differences — the library item already matches this record.</div>}
            {diff.header.length > 0 && (
              <table data-testid="library-diff-header" style={{ borderCollapse: "collapse", marginBottom: 8, width: "100%" }}>
                <thead><tr>{["Field", "Library today", "Will become"].map(h => <th key={h} style={{ textAlign: "left", fontSize: 10.5, color: "var(--text3)", padding: "2px 6px", borderBottom: "1px solid var(--cs-border)" }}>{h}</th>)}</tr></thead>
                <tbody>{diff.header.map(h => (
                  <tr key={h.field}><td style={{ padding: "2px 6px", fontWeight: 700 }}>{h.field}</td><td style={{ padding: "2px 6px", color: "#b91c1c" }}>{h.from ?? "—"}</td><td style={{ padding: "2px 6px", color: "#166534" }}>{h.to ?? "—"}</td></tr>
                ))}</tbody>
              </table>
            )}
            <div style={{ fontSize: 11.5, marginBottom: 8 }}>
              <div><strong>Connections</strong> — the library item's connections are replaced by this record's {mine.length}:</div>
              <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
                {diff.connections.removed.map((c, i) => <li key={`r${i}`} style={{ color: "#b91c1c" }}>− {connectionSummary(c)}</li>)}
                {diff.connections.added.map((c, i) => <li key={`a${i}`} style={{ color: "#166534" }}>+ {connectionSummary(c)}</li>)}
                {diff.connections.unchanged > 0 && <li style={{ color: "var(--text3)" }}>{diff.connections.unchanged} unchanged</li>}
              </ul>
            </div>
            <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 10 }}>Dimensions / weight on the library item are kept as they are. Existing project equipment copied from this item is not affected.</div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => onChoose("update")} style={btnPrimary}>Update library item</button>
              <button onClick={() => setStep("choose")} style={btn}>← Back</button>
              <button onClick={onCancel} style={btn}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
